import { useSearchParams } from "@solidjs/router";
import { type Accessor, createMemo, onMount } from "solid-js";
import {
  normalizeResultTab,
  RESULT_TAB_DEFAULT,
  type ResultTab,
} from "@/domains/company/active-search/utils/result-tabs";

export type ResultTabState = {
  /** The active tab, derived from `?tab=` (URL-as-truth). */
  activeTab: Accessor<ResultTab>;
  /** Switch tabs — pushes a history entry so Back returns to the previous tab. */
  setTab: (tab: ResultTab) => void;
};

/**
 * Tab state for the offer-linked result screen, backed by the `?tab=` URL param
 * (ProfileTabManager idiom). Deeplink `?tab=pool` is honored regardless of plan
 * (D8) — capabilities are NOT an input here; on a free search the param is simply
 * unused because the tabs are hidden.
 *
 * @param tabsVisible reactive accessor — true when the search is offer-linked
 *   (`shouldShowResultTabs`). On mount, if the tabs are visible and no `?tab=` is
 *   present, the URL is normalized to `?tab=ricerca` via `replace` (AC2, no extra
 *   history entry). While hidden, the param is left untouched.
 */
export const useResultTabState = (
  tabsVisible: () => boolean,
): ResultTabState => {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = createMemo(() => normalizeResultTab(searchParams.tab));

  const setTab = (tab: ResultTab) => {
    // Push (no replace): Back returns to the previously selected tab.
    setSearchParams({ tab });
  };

  onMount(() => {
    if (tabsVisible() && !searchParams.tab) {
      setSearchParams({ tab: RESULT_TAB_DEFAULT }, { replace: true });
    }
  });

  return { activeTab, setTab };
};
